import React from "react";
import "./Footer.scss";
import logo from "../assets/cograd-blue-logo.webp";
import gplayBadge from "../assets/svg/gplay.svg";
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter, FaYoutube } from "react-icons/fa";
import { MapPin, Phone, Mail, GraduationCap, Users, Heart, ArrowUpRight } from "lucide-react";
import { isIndependenceWindow, IDAY } from "../lib/independenceDay";
import { impact } from "../data/stats";

const APPS = [
  {
    name: "CoGrad Learning",
    for: "For Students",
    color: "#0041e8",
    bg: "#eff6ff",
    Icon: GraduationCap,
    href: "https://play.google.com/store/apps/details?id=com.learn.Cograd&pcampaignid=web_share",
  },
  {
    name: "Teacher Tracking",
    for: "For Teachers",
    color: "#059669",
    bg: "#f0fdf4",
    Icon: Users,
    href: "https://play.google.com/store/apps/details?id=com.cogradteachingtrack&pcampaignid=web_share",
  },
  {
    name: "Mera Bachha",
    for: "For Parents",
    color: "#d97706",
    bg: "#fffbeb",
    Icon: Heart,
    href: "https://play.google.com/store/apps/details?id=com.merabachhaapp&pcampaignid=web_share",
  },
];

const Footer = () => (
  <footer className="ft">

    {/* ── Main grid ── */}
    <div className="ft__inner">

      {/* Col 1: Brand */}
      <div className="ft__col ft__col--brand">
        <img src={logo} alt="CoGrad" className="ft__logo" />
        <p className="ft__tagline">
          Transforming education across India with NEP-aligned curriculum,
          AI-powered learning, and proven results in {impact.schools.display} schools.
        </p>
        <div className="ft__social">
          <a href="https://www.facebook.com/cograd.in/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebook /></a>
          <a href="https://x.com/cograd_in"             target="_blank" rel="noopener noreferrer" aria-label="Twitter"><FaTwitter /></a>
          <a href="https://in.linkedin.com/company/cograd" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
          <a href="https://www.instagram.com/cograd.in" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram /></a>
          <a href="https://www.youtube.com/c/cograd"    target="_blank" rel="noopener noreferrer" aria-label="YouTube"><FaYoutube /></a>
        </div>
      </div>

      {/* Col 2: Company */}
      <div className="ft__col">
        <h4 className="ft__heading">Company</h4>
        <ul className="ft__list">
          <li><a href="/about-us">About Us</a></li>
          <li><a href="/services">Services</a></li>
          <li><a href="https://medha.cograd.in/" target="_blank" rel="noopener noreferrer">Medha AI</a></li>
          <li><a href="/incubation">Incubation Program</a></li>
          <li><a href="/hackathon">YUVASETU Hackathon</a></li>
          <li><a href="/blog/cograd-featured-by-niti-aayog">CoGrad in the News</a></li>
          <li><a href="/careers">Careers</a></li>
        </ul>
      </div>

      {/* Col 3: Quick Links */}
      <div className="ft__col">
        <h4 className="ft__heading">Quick Links</h4>
        <ul className="ft__list">
          <li><a href="/">Home</a></li>
          <li><a href="https://camp.cograd.in/">Summer Camp</a></li>
          <li><a href="/contact-us">Contact Us</a></li>
          <li><a href="/privacy-policy">Privacy Policy</a></li>
          <li><a href="/terms-conditions">Terms &amp; Conditions</a></li>
        </ul>
      </div>

      {/* Col 4: Contact */}
      <div className="ft__col ft__col--contact">
        <h4 className="ft__heading">Get In Touch</h4>
        <ul className="ft__list ft__list--contact">
          <li className="ft__contact-item">
            <Phone size={13} className="ft__contact-ico" />
            <a href="tel:+919220253001">+91 92202 53001</a>
          </li>
          <li className="ft__contact-item">
            <Mail size={13} className="ft__contact-ico" />
            <a href="mailto:connect@cograd.in">connect@cograd.in</a>
          </li>
          <li className="ft__contact-item ft__contact-item--addr">
            <MapPin size={13} className="ft__contact-ico" style={{ marginTop: 3 }} />
            <span className="ft__address">
              PI SOFTEK, C 56A / 28, C Block, Phase 2,<br />
              Industrial Area, Sector 62,<br />
              Noida, Uttar Pradesh 201309
            </span>
          </li>
        </ul>
      </div>

    </div>

    {/* ── Apps section ── */}
    <div className="ft__apps-section">
      <div className="ft__apps-inner">
        <div className="ft__apps-head">
          <img src={gplayBadge} alt="Get it on Google Play" className="ft__gplay-badge" />
        </div>
        <div className="ft__apps-cards">
          {APPS.map(({ name, for: forLabel, color, bg, Icon, href }) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="ft__app-card"
            >
              <span className="ft__app-card__icon">
                <Icon size={20} strokeWidth={2} />
              </span>
              <div className="ft__app-card__text">
                <span className="ft__app-card__for">{forLabel}</span>
                <span className="ft__app-card__name">{name}</span>
              </div>
              <ArrowUpRight size={16} className="ft__app-card__arrow" strokeWidth={2.5} />
            </a>
          ))}
        </div>
      </div>
    </div>

    {/* ── Independence Day signature (auto-expires) ── */}
    {isIndependenceWindow() && (
      <div className="iday-footnote">
        <span className="iday-footnote__flag"><i /><i /><i /></span>
        <span>
          {IDAY.yearsFree} years of independence. Our next one to win —
          {" "}<strong>freedom from learning poverty.</strong> Built in India, for India.
        </span>
      </div>
    )}

    {/* ── Bottom bar ── */}
    <div className="ft__bottom">
      <p className="ft__copy">
        © {new Date().getFullYear()} Supervision Educare Private Limited. All rights reserved.
      </p>
      <div className="ft__bottom-links">
        <a href="/privacy-policy">Privacy</a>
        <span>·</span>
        <a href="/terms-conditions">Terms</a>
        <span>·</span>
        <a href="/contact-us">Contact</a>
      </div>
    </div>

  </footer>
);

export default Footer;
